import { useCallback } from 'react';
import { useNotification } from '@/common/components/notification/NotificationContext';
import { useIsElectron, useElectronAPI } from '@/context/ElectronContext';
import {
  revealInFileExplorer,
  type RevealTarget,
} from '@/common/utils/fileOperations';

/**
 * Opens a file or folder in the OS file manager with toast feedback
 */
export function useRevealInFileExplorer() {
  const { showSuccess, showError } = useNotification();
  const isElectron = useIsElectron();
  const electronAPI = useElectronAPI();

  const revealInExplorer = useCallback(
    async (target: RevealTarget, successMessage?: string) => {
      if (!target.path) {
        showError('Project location not set. Please locate the project first.');
        return;
      }

      try {
        const result = await revealInFileExplorer(
          target,
          electronAPI,
          isElectron
        );

        if (result.success) {
          showSuccess(successMessage ?? 'Opening file location...');
        } else {
          showError(result.error || 'Failed to open file location');
        }
      } catch {
        showError(
          'Failed to open file location. Please ensure the folder exists locally and the desktop app is connected.'
        );
      }
    },
    [electronAPI, isElectron, showError, showSuccess]
  );

  return { revealInExplorer };
}
